(() => {
  const initialized = new WeakSet();
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /**
   * Returns separate brand spellings for predictive search. They are deliberately
   * requested separately instead of being exposed as Shopify OR syntax in the URL.
   */
  const getBrandSearchVariants = (value) => {
    const normalized = value
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim()
      .replace(/\s+/g, ' ');
    const compact = normalized.replace(/\s/g, '');
    if (compact.length < 3 || compact.length > 24) return [value.trim()];

    const variants = new Set([value.trim(), normalized, compact]);
    if (compact.length > 3 && compact.length <= 12) variants.add(compact.slice(0, -1));
    for (let index = 1; index < compact.length; index += 1) {
      variants.add(`${compact.slice(0, index)}_${compact.slice(index)}`);
    }
    return [...variants].slice(0, 8);
  };

  const initHeader = (header) => {
    if (initialized.has(header)) return;
    initialized.add(header);

    const controller = new AbortController();
    const { signal } = controller;
    const drawer = document.querySelector('[data-mobile-drawer]');
    const backdrop = document.querySelector('[data-drawer-backdrop]');
    const drawerOpen = header.querySelector('[data-drawer-open]');
    const drawerClose = drawer?.querySelector('[data-drawer-close]');
    const searchPanel = header.querySelector('[data-search-panel]');
    const searchInput = header.querySelector('[data-search-input]');
    const searchResults = header.querySelector('[data-search-results]');
    const searchHint = header.querySelector('[data-search-hint]');
    const searchForm = header.querySelector('[data-search-form]');
    const searchOpen = header.querySelector('[data-search-open]');
    const searchClose = header.querySelector('[data-search-close]');
    const searchDismiss = header.querySelector('[data-search-dismiss]');
    let lastScroll = window.scrollY;
    let drawerReturnFocus = null;
    let searchReturnFocus = null;
    let searchTimer;
    let searchRequest;

    const closeMegas = (except = null) => {
      header.querySelectorAll('[data-mega-toggle]').forEach((button) => {
        const panel = document.getElementById(button.getAttribute('aria-controls'));
        if (panel === except) return;
        button.setAttribute('aria-expanded', 'false');
        if (panel) panel.hidden = true;
      });
    };

    header.querySelectorAll('[data-mega-toggle]').forEach((button) => {
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      if (!panel) return;
      const open = () => {
        closeMegas(panel);
        button.setAttribute('aria-expanded', 'true');
        panel.hidden = false;
      };
      const close = () => {
        button.setAttribute('aria-expanded', 'false');
        panel.hidden = true;
      };

      button.addEventListener('click', () => button.getAttribute('aria-expanded') === 'true' ? close() : open(), { signal });
      button.parentElement.addEventListener('mouseenter', open, { signal });
      button.parentElement.addEventListener('mouseleave', close, { signal });
      button.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          open();
          panel.querySelector(focusableSelector)?.focus();
        }
        if (event.key === 'Escape') close();
      }, { signal });
      panel.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          close();
          button.focus();
        }
      }, { signal });
    });

    const openDrawer = () => {
      if (!drawer || !backdrop) return;
      drawerReturnFocus = document.activeElement;
      drawer.hidden = false;
      backdrop.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
      drawerOpen?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('dc-menu-open');
      requestAnimationFrame(() => {
        drawer.classList.add('is-open');
        backdrop.classList.add('is-open');
        drawerClose?.focus();
      });
    };

    const closeDrawer = (restoreFocus = true) => {
      if (!drawer || !backdrop) return;
      drawer.classList.remove('is-open');
      backdrop.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      drawerOpen?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('dc-menu-open');
      window.setTimeout(() => {
        backdrop.hidden = true;
        if (restoreFocus && drawerReturnFocus instanceof HTMLElement) drawerReturnFocus.focus();
      }, 300);
    };

    drawerOpen?.addEventListener('click', openDrawer, { signal });
    drawerClose?.addEventListener('click', () => closeDrawer(), { signal });
    backdrop?.addEventListener('click', () => closeDrawer(), { signal });

    drawer?.querySelectorAll('[data-accordion-toggle]').forEach((button) => {
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!expanded));
        if (panel) panel.hidden = expanded;
      }, { signal });
    });

    const openSearch = () => {
      closeMegas();
      searchReturnFocus = document.activeElement;
      searchPanel.hidden = false;
      document.body.classList.add('dc-search-open');
      searchOpen?.setAttribute('aria-expanded', 'true');
      searchInput?.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => searchInput?.focus());
    };

    const closeSearch = (restoreFocus = true) => {
      searchPanel.hidden = true;
      document.body.classList.remove('dc-search-open');
      searchOpen?.setAttribute('aria-expanded', 'false');
      searchInput?.setAttribute('aria-expanded', 'false');
      searchRequest?.abort();
      if (restoreFocus && searchReturnFocus instanceof HTMLElement) searchReturnFocus.focus();
    };

    searchOpen?.addEventListener('click', openSearch, { signal });
    searchClose?.addEventListener('click', () => closeSearch(), { signal });
    searchDismiss?.addEventListener('click', () => closeSearch(), { signal });
    const setSearchStatus = (message, isError = false) => {
      searchResults.replaceChildren();
      const status = document.createElement('p');
      status.className = 'dc-search__status';
      if (isError) status.setAttribute('role', 'alert');
      status.textContent = message;
      searchResults.append(status);
    };

    const createSearchResult = (item, type) => {
      const link = document.createElement('a');
      link.className = 'dc-search-result';
      link.href = item.url;
      link.setAttribute('role', 'option');

      const media = document.createElement('span');
      media.className = 'dc-search-result__media';
      const imageUrl = typeof item.image === 'string' ? item.image : item.image?.url;
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = '';
        image.loading = 'lazy';
        media.append(image);
      } else {
        media.textContent = type === 'Product' ? 'DC' : '↗';
      }

      const copy = document.createElement('span');
      copy.className = 'dc-search-result__copy';
      const label = document.createElement('span');
      label.className = 'dc-search-result__type';
      label.textContent = type;
      const title = document.createElement('strong');
      title.textContent = item.title;
      copy.append(label, title);

      const arrow = document.createElement('span');
      arrow.className = 'dc-search-result__arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '→';
      link.append(media, copy, arrow);
      return link;
    };

    const runSearch = async () => {
      const query = searchInput.value.trim();
      searchHint.hidden = query.length >= 2;
      if (query.length < 2) {
        searchRequest?.abort();
        searchResults.replaceChildren();
        return;
      }
      searchRequest?.abort();
      searchRequest = new AbortController();
      setSearchStatus('Searching IMPORTED SOUQ…');
      const root = window.Shopify?.routes?.root || '/';
      const searchVariants = getBrandSearchVariants(query);
      const fetchSuggestions = async (searchQuery, limit = 6) => {
        const params = new URLSearchParams({
          q: searchQuery,
          'resources[type]': 'product,collection',
          'resources[limit]': String(limit),
          'resources[options][fields]': 'title,vendor,product_type,variants.title',
          'resources[options][prefix]': 'last',
          'resources[options][typo_tolerance]': 'true',
          'resources[options][unavailable_products]': 'last'
        });
        const response = await fetch(`${root}search/suggest.json?${params}`, {
          signal: searchRequest.signal,
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error('Search request failed');
        return response.json();
      };

      try {
        // Keep exact results first, then merge separate variation requests. Shopify's
        // search parser can return zero for large OR expressions, so we never send one.
        const responses = await Promise.all(searchVariants.map((variant) => fetchSuggestions(variant)));
        const resultSets = responses.map((response) => response.resources?.results || {});
        const uniqueByUrl = (items) => [...new Map(items.map((item) => [item.url, item])).values()];
        const products = uniqueByUrl(resultSets.flatMap((results) => results.products || [])).slice(0, 6);
        const collections = uniqueByUrl(resultSets.flatMap((results) => results.collections || [])).slice(0, 6);
        searchResults.replaceChildren();

        if (!products.length && !collections.length) {
          setSearchStatus(`No matches for “${query}”. Try another product or brand.`);
          return;
        }

        const grid = document.createElement('div');
        grid.className = 'dc-search__grid';
        products.forEach((product) => grid.append(createSearchResult(product, 'Product')));
        collections.forEach((collection) => grid.append(createSearchResult(collection, 'Collection')));
        searchResults.append(grid);

        const viewAll = document.createElement('a');
        viewAll.className = 'dc-search__view-all';
        const viewAllParams = new URLSearchParams({
          q: query,
          type: 'product',
          'options[prefix]': 'last'
        });
        viewAll.href = `${root}search?${viewAllParams}`;
        viewAll.textContent = `View all results for “${query}”`;
        searchResults.append(viewAll);
      } catch (error) {
        if (error.name !== 'AbortError') setSearchStatus('Search is temporarily unavailable. Press Enter to search instead.', true);
      }
    };

    searchForm?.addEventListener('submit', (event) => {
      if (searchInput.value.trim()) return;
      event.preventDefault();
      searchInput.focus();
    }, { signal });
    searchInput?.addEventListener('input', () => {
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(runSearch, 250);
    }, { signal });
    searchInput?.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowDown') return;
      const firstResult = searchResults.querySelector(focusableSelector);
      if (firstResult) {
        event.preventDefault();
        firstResult.focus();
      }
    }, { signal });
    searchPanel?.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const focusable = [...searchPanel.querySelectorAll(focusableSelector)].filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }, { signal });

    document.addEventListener('click', (event) => {
      if (!header.contains(event.target)) closeMegas();
    }, { signal });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (!searchPanel.hidden) {
        closeSearch();
      } else if (drawer?.classList.contains('is-open')) {
        closeDrawer();
      } else {
        closeMegas();
      }
    }, { signal });

    drawer?.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || !drawer.classList.contains('is-open')) return;
      const focusable = [...drawer.querySelectorAll(focusableSelector)].filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }, { signal });

    if (header.dataset.sticky === 'true') {
      const updateHeader = () => {
        const current = Math.max(window.scrollY, 0);
        header.classList.toggle('is-scrolled', current > 24);
        const blocked = drawer?.classList.contains('is-open') || !searchPanel.hidden;
        if (!blocked && current > 120 && current > lastScroll + 7) header.classList.add('is-hidden');
        if (current < lastScroll - 7 || current <= 120) header.classList.remove('is-hidden');
        lastScroll = current;
      };
      window.addEventListener('scroll', updateHeader, { passive: true, signal });
      updateHeader();
    }

    header.addEventListener('shopify:block:select', () => header.classList.remove('is-hidden'), { signal });
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-dc-header]').forEach(initHeader);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
